if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      static submissionPending = false;

      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = this.closest('[data-release-blocked="true"]') !== null;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.closest('[data-release-blocked="true"]') ||
          ProductForm.submissionPending ||
          (typeof CartItems !== 'undefined' && CartItems.mutationPending) ||
          this.submitButton.disabled ||
          this.submitButton.getAttribute('aria-disabled') === 'true' ||
          this.variantIdInput.disabled ||
          !this.variantIdInput.value
        ) return;
        if (!this.form.reportValidity()) return;

        const formData = new FormData(this.form);
        const submittedQuantity = formData.get('quantity');
        const quantity = submittedQuantity === null ? 1 : Number(submittedQuantity);
        if (!Number.isInteger(quantity) || quantity <= 0) {
          this.handleErrorMessage(window.fujiStrings?.quantityError || window.cartStrings.error);
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        let addSucceeded = false;
        let submitFailed = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.submitButton.setAttribute('aria-busy', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        ProductForm.submissionPending = true;
        const releaseMutation = typeof CartItems !== 'undefined' ? CartItems.beginMutation() : null;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        return fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const result = await response.json();
            if (!response.ok && !result?.status) throw new Error(`Cart response ${response.status}`);
            if (!result || typeof result !== 'object') throw new Error('Invalid cart response');
            return result;
          })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              const errorMessage = typeof response.description === 'string'
                ? response.description
                : typeof response.message === 'string' ? response.message : window.cartStrings.error;
              submitFailed = true;
              if (this.variantIdInput.value === variantId) this.handleErrorMessage(errorMessage);
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage));
              if (Number(response.status) === 422) {
                releaseMutation?.();
                this.publishCartUpdate({
                  source: 'product-form-error',
                  productVariantId: variantId,
                });
              }
              return;
            } else if (!this.cart) {
              addSucceeded = true;
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            addSucceeded = true;
            this.cart.classList.remove('is-empty');
            this.resolveCartLinesUpdate(linesUpdateDeferred);

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              quickAddModal.hide(true);
              await new Promise(resolve => setTimeout(resolve));
            }
            CartPerformance.measure("add:paint-updated-sections", () => {
              this.cart.renderContents(response);
            });
            releaseMutation?.();
            this.publishCartUpdate({
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            });
          })
          .catch((error) => {
            if (addSucceeded) {
              window.location.assign(window.routes.cart_url);
              return;
            }
            submitFailed = true;
            if (this.variantIdInput.value === variantId) this.handleErrorMessage(window.cartStrings.error);
            this.dispatchCartErrorEvent(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(error);
          })
          .finally(() => {
            releaseMutation?.();
            ProductForm.submissionPending = false;
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.removeAttribute('aria-busy');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            if (submitFailed && this.variantIdInput.value === variantId &&
              (document.activeElement === document.body || document.activeElement === this.submitButton)) {
              this.submitButton.focus({ preventScroll: true });
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      publishCartUpdate(data) {
        try {
          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, data))
            .then(() => CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker))
            .catch((error) => console.warn('Cart details refresh failed.', error));
        } catch (error) {
          console.warn('Cart details refresh failed.', error);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          const message = document.createElement('span');
          message.className = 'product-form__error-message';
          this.errorMessageWrapper.append(message);
          this.prepend(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const release = this.closest('[data-release-blocked="true"]');
        if (release) {
          disable = true;
          text = window.fujiStrings?.comingSoon || this.submitButtonText.textContent;
          this.variantIdInput.disabled = true;
        }
        this.error = false;
        this.submitButtonText.classList.remove('hidden');
        this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
        if (!this.submitButton.classList.contains('loading')) this.submitButton.removeAttribute('aria-disabled');
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
